import React, { useState } from 'react';
import { Project, ProjectOtherCost } from '../../../types/Project';
import { Alert, Button, Form, RowActions, DataTable, type DataTableColumn } from '@/components/ui';
import { describeActionError } from '../../../utils/loadError';
import projectService from '../../../services/projectService';
import ResponsiveModal from '../../../components/ResponsiveModal';
import { useClientSort } from '../../../utils/useClientSort';
import { formatDateForDisplayUTC } from '../../../utils/dateUtils';

interface OtherCostsContentProps {
  project: Project;
  onUpdate: (updatedProject: Project) => void;
}

const SORT_VALUES: Record<string, (c: ProjectOtherCost) => string | number | null | undefined> = {
  date: (c) => (c.date ? new Date(c.date).getTime() : null),
  description: (c) => c.description,
  dollarAmount: (c) => c.dollarAmount,
  markup: (c) => c.markup,
  amount: (c) => c.amount,
};

const OtherCostsContent: React.FC<OtherCostsContentProps> = ({ project, onUpdate }) => {
  const { sorted, sortBy, sortDir, onSort } = useClientSort(project.projectOtherCosts ?? [], SORT_VALUES);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [costToDelete, setCostToDelete] = useState<number | null>(null);
  const [editingCost, setEditingCost] = useState<ProjectOtherCost | null>(null);
  const [formData, setFormData] = useState({
    date: '',
    dollarAmount: '',
    markup: '',
    description: ''
  });
  // Inline feedback (FORM-01 / STATE-06): per-field errors, and save / delete failures in their dialogs.
  const [fieldErrors, setFieldErrors] = useState<{ dollarAmount?: string; markup?: string }>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Helper function to safely format date for display
  const formatDateForDisplay = (dateValue: string | Date | null | undefined): string => {
    if (!dateValue) return 'Not set';
    
    // Values arrive both as bare YYYY-MM-DD and as UTC-midnight ISO strings. The shared helper
    // normalizes both to a UTC date before formatting, so a cost dated the 1st never displays
    // as the 31st for anyone west of UTC.
    const formatted = formatDateForDisplayUTC(dateValue);
    return formatted === 'Unknown' ? 'Not set' : formatted;
  };

  // Helper function to safely format date for input field
  const formatDateForInput = (dateValue: string | Date | null | undefined): string => {
    if (!dateValue) return '';
    
    try {
      let date: Date;
      
      if (dateValue instanceof Date) {
        date = dateValue;
      } else if (typeof dateValue === 'string') {
        // If it's already an ISO string, use it directly
        if (dateValue.includes('T')) {
          date = new Date(dateValue);
        } else {
          // If it's just a date string, add time
          date = new Date(dateValue + 'T00:00:00');
        }
      } else {
        return '';
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return '';
      }
      
      return date.toISOString().split('T')[0];
    } catch (error) {
      console.error('Error formatting date for input:', error, dateValue);
      return '';
    }
  };

  const handleAddEdit = (cost?: ProjectOtherCost) => {
    if (cost) {
      setEditingCost(cost);
      setFormData({
        date: formatDateForInput(cost.date),
        dollarAmount: cost.dollarAmount?.toString() || '',
        markup: cost.markup?.toString() || '',
        description: cost.description
      });
    } else {
      setEditingCost(null);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        dollarAmount: '',
        markup: '',
        description: ''
      });
    }
    setFieldErrors({});
    setSaveError(null);
    setShowModal(true);
  };

  const handleDelete = (costId: number) => {
    setCostToDelete(costId);
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!costToDelete) return;

    try {
      const updatedProject = await projectService.deleteProjectOtherCost(project.id, costToDelete);
      onUpdate(updatedProject);
      setShowDeleteModal(false);
      setCostToDelete(null);
    } catch (error) {
      console.error('Error deleting cost:', error);
      setDeleteError(describeActionError(error, "Couldn't delete the cost. Try again."));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setSaveError(null);

    // Validate each required number and show the problem under its own field (FORM-01).
    const dollarAmount = formData.dollarAmount?.trim();
    const markup = formData.markup?.trim();
    const dollarAmountNum = parseFloat(dollarAmount);
    const markupNum = parseFloat(markup);
    const numberError = (raw: string, num: number, label: string) => {
      if (!raw) return `${label} required`;
      if (isNaN(num)) return `${label} must be a number`;
      if (num < 0) return `${label} can't be negative`;
      return undefined;
    };
    const errors = {
      dollarAmount: numberError(dollarAmount, dollarAmountNum, 'Dollar amount'),
      markup: numberError(markup, markupNum, 'Markup'),
    };
    setFieldErrors(errors);
    if (errors.dollarAmount || errors.markup) {
      document.getElementById(errors.dollarAmount ? 'otherCostDollarAmount' : 'otherCostMarkup')?.focus();
      return;
    }

    try {
      const costData = {
        date: new Date(formData.date + 'T00:00:00'),
        dollarAmount: dollarAmountNum,
        markup: markupNum,
        description: formData.description.trim()
      };

      if (editingCost) {
        const updatedProject = await projectService.updateProjectOtherCost(
          project.id,
          editingCost.id,
          costData
        );
        onUpdate(updatedProject);
      } else {
        const updatedProject = await projectService.createProjectOtherCost(
          project.id,
          costData
        );
        onUpdate(updatedProject);
      }
      setShowModal(false);
    } catch (error) {
      console.error('Error saving cost:', error);
      setSaveError(describeActionError(error, "Couldn't save the cost. Try again."));
    }
  };

  // Description is the identifying label for a cost line, so it's the card title; Date is the
  // secondary line. The three money fields are presentation-only here — no calculation moved.
  const otherCostColumns: DataTableColumn<ProjectOtherCost>[] = [
    {
      key: 'description',
      header: 'Description',
      sortable: true,
      mobile: 'title',
      cell: (cost) => cost.description,
    },
    {
      key: 'date',
      header: 'Date',
      sortable: true,
      mobile: 'subtitle',
      cell: (cost) => formatDateForDisplay(cost.date),
    },
    {
      key: 'dollarAmount',
      header: 'Dollar Amount',
      sortable: true,
      cell: (cost) => (cost.dollarAmount !== undefined && cost.dollarAmount !== null ? `$${cost.dollarAmount.toFixed(2)}` : '-'),
    },
    {
      key: 'markup',
      header: 'Markup (%)',
      sortable: true,
      cell: (cost) => (cost.markup !== undefined && cost.markup !== null ? `${cost.markup.toFixed(1)}%` : '-'),
    },
    {
      key: 'amount',
      header: 'Total',
      sortable: true,
      cell: (cost) => (cost.amount !== undefined && cost.amount !== null ? `$${cost.amount.toFixed(2)}` : '$0.00'),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      mobile: 'trailing',
      cell: (cost) => (
        <RowActions
          actions={[
            { kind: 'edit', label: 'Edit Cost', onClick: () => handleAddEdit(cost) },
            { kind: 'delete', label: 'Delete Cost', onClick: () => handleDelete(cost.id) },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-3">
        <h5 className="tw-mb-0">Other / Miscellaneous Costs</h5>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => handleAddEdit()}
        >
          <i className="bi bi-plus-lg tw-mr-1"></i> Add Cost
        </Button>
      </div>

      <DataTable
        columns={otherCostColumns}
        data={sorted}
        rowKey={(cost) => cost.id}
        hover
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={onSort}
        emptyMessage="No costs added yet"
      />

      {/* Add/Edit Modal */}
      <ResponsiveModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingCost ? 'Edit Cost' : 'Add Cost'}
        size="medium"
        footer={
          <div className="tw-flex tw-gap-2">
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit}>
              {editingCost ? 'Save Changes' : 'Add Cost'}
            </Button>
          </div>
        }
      >
        <Form onSubmit={handleSubmit}>
          {saveError && (
            <Alert variant="danger" role="alert" className="tw-mb-3">
              {saveError}
            </Alert>
          )}
          <Form.Group className="tw-mb-3">
            <Form.Label>Date</Form.Label>
            <Form.Control
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
            />
          </Form.Group>
          <Form.Group className="tw-mb-3">
            <Form.Label>Dollar Amount <span className="tw-text-danger">*</span></Form.Label>
            <Form.Control
              id="otherCostDollarAmount"
              type="number"
              step="0.01"
              min="0"
              value={formData.dollarAmount}
              onChange={(e) => {
                setFormData({ ...formData, dollarAmount: e.target.value });
                if (fieldErrors.dollarAmount) setFieldErrors((prev) => ({ ...prev, dollarAmount: undefined }));
              }}
              placeholder="Enter base dollar amount"
              required
              isInvalid={!!fieldErrors.dollarAmount}
              aria-invalid={!!fieldErrors.dollarAmount}
            />
            {fieldErrors.dollarAmount && (
              <Form.Control.Feedback type="invalid">{fieldErrors.dollarAmount}</Form.Control.Feedback>
            )}
          </Form.Group>
          <Form.Group className="tw-mb-3">
            <Form.Label>Markup (%) <span className="tw-text-danger">*</span></Form.Label>
            <Form.Control
              id="otherCostMarkup"
              type="number"
              step="0.1"
              min="0"
              max="1000"
              value={formData.markup}
              onChange={(e) => {
                setFormData({ ...formData, markup: e.target.value });
                if (fieldErrors.markup) setFieldErrors((prev) => ({ ...prev, markup: undefined }));
              }}
              placeholder="Enter markup percentage (e.g., 15.5 for 15.5%)"
              required
              isInvalid={!!fieldErrors.markup}
              aria-invalid={!!fieldErrors.markup}
            />
            {fieldErrors.markup && (
              <Form.Control.Feedback type="invalid">{fieldErrors.markup}</Form.Control.Feedback>
            )}
          </Form.Group>
          <Form.Group className="tw-mb-3">
            <Form.Label>Calculated Total</Form.Label>
            <Form.Control
              type="text"
              value={
                formData.dollarAmount && formData.markup && !isNaN(parseFloat(formData.dollarAmount)) && !isNaN(parseFloat(formData.markup))
                  ? `$${(parseFloat(formData.dollarAmount) * (1 + parseFloat(formData.markup) / 100)).toFixed(2)}`
                  : '$0.00'
              }
              readOnly
              className="tw-bg-surface-alt"
            />
            {formData.dollarAmount && formData.markup && !isNaN(parseFloat(formData.dollarAmount)) && !isNaN(parseFloat(formData.markup)) && (
              <Form.Text className="tw-text-content-muted">
                Calculated as: ${formData.dollarAmount} × (1 + {formData.markup}% / 100) = ${(parseFloat(formData.dollarAmount) * (1 + parseFloat(formData.markup) / 100)).toFixed(2)}
              </Form.Text>
            )}
          </Form.Group>
          <Form.Group className="tw-mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
            />
          </Form.Group>
        </Form>
      </ResponsiveModal>

      {/* Delete Confirmation Modal */}
      <ResponsiveModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirm Delete"
        size="small"
        footer={
          <div className="tw-flex tw-gap-2">
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete
            </Button>
          </div>
        }
      >
        {deleteError && (
          <Alert variant="danger" role="alert" className="tw-mb-3">
            {deleteError}
          </Alert>
        )}
        <p>Are you sure you want to delete this cost?</p>
      </ResponsiveModal>
    </>
  );
};

export default OtherCostsContent; 